import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  Bookmark,
  EyeOff,
  Flag,
  Languages,
  MoreHorizontal,
  Share2,
  Trash2,
} from "lucide-react";

import { clubAvatarInitial, resolveClubMediaUrl } from "../../features/comments/utils/clubFeedPostUtils.js";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import {
  clubPostCardAvatarClass,
  clubPostOptionsMenuClass,
  clubPostOptionsTriggerClass,
} from "./feedPostCardPresentation";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

/** Readable fallback when API has slug but no display name yet. */
export function humanizeClubSlug(slug) {
  const s = String(slug || "").trim();
  if (!s) return "";
  return s
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function pickClubContextSlug(post, clubSlugProp) {
  return String(clubSlugProp || post?.feed_club_slug || post?.club_slug || "").trim();
}

function pickClubDisplayName(post, club, clubSlugProp) {
  const fromClub = club?.name != null && String(club.name).trim() !== "" ? String(club.name).trim() : "";
  const fromPost =
    post?.feed_club_name != null && String(post.feed_club_name).trim() !== ""
      ? String(post.feed_club_name).trim()
      : "";
  const slug = pickClubContextSlug(post, clubSlugProp);
  return fromClub || fromPost || humanizeClubSlug(slug);
}

function pickClubAvatarUrl(post, club) {
  const raw =
    club?.avatar_image_url ??
    club?.avatarImageUrl ??
    post?.feed_club_avatar_url ??
    post?.club_avatar_image_url ??
    null;
  return resolveClubMediaUrl(raw);
}

const placeholderMenuItemClass =
  "w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm text-white/45 transition-colors duration-150 ease-in-out hover:bg-white/[0.05] hover:text-white/65 cursor-default";

const primaryMenuItemClass =
  "w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm text-gray-100 transition-colors duration-150 ease-in-out hover:bg-white/[0.07] active:bg-white/[0.04]";

/**
 * Reddit-style post header: primary surface identity (club or user), secondary author · relative time,
 * overflow menu (real share/edit/delete + prepared placeholder slots).
 *
 * @param {"feed"|"thread"} variant
 * @param {boolean} [menuOpenProp] — controlled menu open (thread shells)
 * @param {function} [setMenuOpenProp]
 * @param {import("react").MutableRefObject} [menuRefProp]
 */
export default function FeedPostCardHeader({
  post,
  club = null,
  clubSlug = "",
  variant = "feed",
  menuOpen: menuOpenProp,
  setMenuOpen: setMenuOpenProp,
  menuRef: menuRefProp,
  onShare,
  onDelete,
  canDelete = false,
  deleteBusy = false,
  onEditPost,
  /** When false, omit overflow trigger (rare). */
  showOverflowMenu = true,
}) {
  const internalMenuRef = useRef(null);
  const menuRef = menuRefProp || internalMenuRef;

  const [menuOpenLocal, setMenuOpenLocal] = useState(false);
  const isMenuControlled = menuOpenProp !== undefined && typeof setMenuOpenProp === "function";
  const menuOpen = isMenuControlled ? menuOpenProp : menuOpenLocal;
  const setMenuOpen = isMenuControlled ? setMenuOpenProp : setMenuOpenLocal;

  useEffect(() => {
    if (isMenuControlled || !menuOpen) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isMenuControlled, menuOpen, menuRef, setMenuOpen]);

  const contextSlug = pickClubContextSlug(post, clubSlug);
  const clubDisplayName = pickClubDisplayName(post, club, clubSlug);
  const clubAvatarUrl = pickClubAvatarUrl(post, club);
  const isClubAttached = Boolean(contextSlug) && Boolean(clubDisplayName || contextSlug);

  const userAvatar = resolveClubMediaUrl(post?.profile_picture);
  const username = post?.username || "Member";
  const uid = post?.user_id != null && Number.isFinite(Number(post.user_id)) ? Number(post.user_id) : null;
  const timeLabel = formatRelativeTime(post?.created_at);
  const clubPath = contextSlug ? `/clubs/${encodeURIComponent(contextSlug)}` : "#";

  let createdAtIso = "";
  try {
    if (post?.created_at != null && post.created_at !== "") {
      const t = new Date(post.created_at).getTime();
      if (!Number.isNaN(t)) createdAtIso = new Date(post.created_at).toISOString();
    }
  } catch {
    createdAtIso = "";
  }

  const closeMenu = () => setMenuOpen(false);

  /** Prepared UI only — wire to backend when Follow post ships. */
  const onPlaceholder = (e) => {
    e.stopPropagation();
    closeMenu();
  };

  const primaryTextClass =
    variant === "thread"
      ? "text-[15px] sm:text-base font-semibold text-white tracking-tight truncate"
      : "text-sm font-semibold text-white tracking-tight truncate";

  const secondaryAuthorClass =
    "font-medium text-cyan-400/95 hover:text-cyan-300 truncate max-w-[14rem] sm:max-w-none";

  return (
    <div className={cn("flex items-start justify-between gap-3 min-w-0")}>
      <div className="flex gap-3 min-w-0 flex-1 rounded-lg -m-1 p-1">
        <div className={cn(clubPostCardAvatarClass, "pointer-events-none")}>
          {isClubAttached ? (
            clubAvatarUrl ? (
              <img src={clubAvatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              clubAvatarInitial(clubDisplayName || contextSlug || "C")
            )
          ) : userAvatar ? (
            <img src={userAvatar} alt="" className="h-full w-full object-cover" />
          ) : (
            clubAvatarInitial(username)
          )}
        </div>

        <div className="min-w-0 flex-1 pt-0.5">
          {isClubAttached ? (
            <>
              <Link
                to={clubPath}
                onClick={(e) => e.stopPropagation()}
                className={cn(primaryTextClass, "block hover:text-emerald-200/95 transition-colors")}
              >
                {clubDisplayName || humanizeClubSlug(contextSlug)}
              </Link>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] sm:text-[13px] text-gray-400 min-w-0">
                {uid != null ? (
                  <Link
                    to={`/hub/${encodeURIComponent(String(uid))}`}
                    onClick={(e) => e.stopPropagation()}
                    className={secondaryAuthorClass}
                  >
                    {username}
                  </Link>
                ) : (
                  <span className="font-medium text-cyan-400/95 truncate max-w-[14rem] sm:max-w-none">{username}</span>
                )}
                <span className="text-gray-600 shrink-0" aria-hidden>
                  ·
                </span>
                <time className="text-gray-400/90 tabular-nums tracking-tight shrink-0" dateTime={createdAtIso || undefined}>
                  {timeLabel}
                </time>
              </div>
            </>
          ) : (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] sm:text-[13px] text-gray-400 min-w-0">
              {uid != null ? (
                <Link
                  to={`/hub/${encodeURIComponent(String(uid))}`}
                  onClick={(e) => e.stopPropagation()}
                  className={secondaryAuthorClass}
                >
                  {username}
                </Link>
              ) : (
                <span className="font-medium text-cyan-400/95 truncate max-w-[14rem] sm:max-w-none">{username}</span>
              )}
              <span className="text-gray-600 shrink-0" aria-hidden>
                ·
              </span>
              <time className="text-gray-400/90 tabular-nums tracking-tight shrink-0" dateTime={createdAtIso || undefined}>
                {timeLabel}
              </time>
            </div>
          )}
        </div>
      </div>

      {showOverflowMenu ? (
        <div className="relative shrink-0" ref={menuRef}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((o) => !o);
            }}
            className={clubPostOptionsTriggerClass(menuOpen)}
            title="Post options"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-label="Post options"
          >
            <MoreHorizontal className="h-4 w-4" strokeWidth={2} />
          </button>
          {menuOpen ? (
            <div className={clubPostOptionsMenuClass} role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  closeMenu();
                  onShare?.(e);
                }}
                className={primaryMenuItemClass}
              >
                <Share2 className="h-4 w-4 shrink-0 text-gray-400" strokeWidth={2} />
                Share or copy link
              </button>

              <div className="my-1 h-px bg-white/[0.08]" role="separator" />

              <button
                type="button"
                role="menuitem"
                title="Coming soon"
                onClick={onPlaceholder}
                className={placeholderMenuItemClass}
              >
                <Bell className="h-4 w-4 shrink-0 text-white/35" strokeWidth={2} />
                Follow post
                <span className="sr-only">(UI prepared, not wired)</span>
              </button>
              <button
                type="button"
                role="menuitem"
                title="Coming soon"
                onClick={onPlaceholder}
                className={placeholderMenuItemClass}
              >
                <Bookmark className="h-4 w-4 shrink-0 text-white/35" strokeWidth={2} />
                Save
                <span className="sr-only">(UI prepared, not wired)</span>
              </button>
              <button
                type="button"
                role="menuitem"
                title="Coming soon"
                onClick={onPlaceholder}
                className={placeholderMenuItemClass}
              >
                <EyeOff className="h-4 w-4 shrink-0 text-white/35" strokeWidth={2} aria-hidden />
                Hide
                <span className="sr-only">(UI prepared, not wired)</span>
              </button>

              <div className="my-1 h-px bg-white/[0.08]" role="separator" />

              <button
                type="button"
                role="menuitem"
                title="Coming soon"
                onClick={onPlaceholder}
                className={placeholderMenuItemClass}
              >
                <Languages className="h-4 w-4 shrink-0 text-white/35" strokeWidth={2} />
                Language & translations
                <span className="sr-only">(UI prepared, not wired)</span>
              </button>

              <div className="my-1 h-px bg-white/[0.08]" role="separator" />

              <button
                type="button"
                role="menuitem"
                title="Coming soon"
                onClick={onPlaceholder}
                className={placeholderMenuItemClass}
              >
                <Flag className="h-4 w-4 shrink-0 text-white/35" strokeWidth={2} />
                Report
                <span className="sr-only">(UI prepared, not wired)</span>
              </button>

              {onEditPost ? (
                <>
                  <div className="my-1 h-px bg-white/[0.08]" role="separator" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={(e) => {
                      e.stopPropagation();
                      closeMenu();
                      onEditPost(post);
                    }}
                    className={primaryMenuItemClass}
                  >
                    Edit post
                  </button>
                </>
              ) : null}

              {canDelete ? (
                <>
                  <div className="my-1 h-px bg-white/[0.08]" role="separator" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={(e) => {
                      e.stopPropagation();
                      closeMenu();
                      onDelete?.(e);
                    }}
                    disabled={deleteBusy}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm text-red-300/95 transition-colors duration-150 ease-in-out hover:bg-red-500/12 active:bg-red-500/8 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <Trash2 className="h-4 w-4 shrink-0 opacity-90" strokeWidth={2} />
                    {deleteBusy ? "Deleting…" : "Delete post"}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
